import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { CollapsibleTableContainer } from './CollapsibleTableContainer';
import { RowComp } from './RowComp';
import { MainTableCellStyle, MainTableHeadStyle, NoRecordsStyle } from './styled';

export type CollapsibleTableCell = {
  align?: "center" | "left" | "right" | "inherit" | "justify";
  title: string | JSX.Element;
}

export type DataAutomation = {
  table?: string,
  header?: string,
  rows?: string,
  columns: string[],
  collapsibleRow?: string,
  noRecords?: string,
  tBody?: string,
}
type CollapsibleTableProps<P> = {
  cols?: CollapsibleTableCell[];
  rows: P[];
  hiddenColsOnMobile?: number[];
  isOneLevel?: boolean;
  isThreeLevel?: boolean;
  dataAutomation?: DataAutomation;
  noDataMsg?: string;
  noColsDivider?: boolean;
  // The list of row indexes will be rendered as footer rows
  footerRowIndexes?: number[];
  // The two dimensional list of row indexes will be rendered as collapsible footer rows
  // First index is the outer row index and the second index is the inner row index
  allInnerFooterRowIndexes?: number[][];
  // The list of row indexes that will be expanded by default
  openByDefaultIndexes?: number[];
  // The list of row index that will only display the row header
  displayHeaderOnlyIndexes?: number[];
  size?: "custom" | "medium" | "small";
  sxSmall?: boolean
};

const getColHeaderLength = (title: string | JSX.Element) => {
  if (typeof title === "string") {
    return title.length * 2 > 20 ? 20 : title.length * 2;
  } else {
    return 20;
  }
}

export const CollapsibleTable = <P extends {}>({
  cols = [],
  rows = [],
  hiddenColsOnMobile = [],
  isOneLevel = false,
  isThreeLevel = false,
  dataAutomation,
  noDataMsg = 'No records',
  noColsDivider = false,
  footerRowIndexes,
  allInnerFooterRowIndexes,
  openByDefaultIndexes,
  displayHeaderOnlyIndexes,
  size = "medium",
  sxSmall = false
}: CollapsibleTableProps<P>) => {
  if (!rows.length) {
    return (
      <Typography
        sx={NoRecordsStyle}
        data-automation={dataAutomation?.noRecords ?? 'no-records'}
      >
        {noDataMsg}
      </Typography>
    )
  }
  const tableClassName = isOneLevel ? 'one-level' : isThreeLevel ? 'three-level' : 'two-level';
  return (
    <Table size={size} className={`${tableClassName} ${sxSmall ? 'sx-small' : ''}`} data-automation={dataAutomation?.table ?? 'table'} aria-label="collapsible table">
      {!isThreeLevel && <TableHead data-automation={dataAutomation?.header ?? ''}>
        <TableRow className={isOneLevel ? 'oneLevel' : ''} sx={MainTableHeadStyle}>
          {cols.map(({ align = 'left', title }, i) => (
            <TableCell
              colSpan={noColsDivider ? 2 : 1}
              data-automation={dataAutomation?.columns[i] ?? null}
              className={hiddenColsOnMobile.includes(i) ? 'hiddenOnMobile' : ''}
              sx={MainTableCellStyle(sxSmall ? 0 : getColHeaderLength(title))}
              key={i}
              align={align}
            >
              {typeof title === "string" ?
                <Typography>{title}</Typography> :
                <Typography component={"div"} >{title}</Typography>
              }

            </TableCell>
          ))}
        </TableRow>
      </TableHead>}
      <TableBody data-automation={dataAutomation?.tBody ?? 'tBody'}>
        {rows.map((row: { [key: string]: any }, id) => {
          const props = {
            isThreeLevel,
            isOneLevel,
            key: id,
            row: isThreeLevel ? Object.keys(row).filter(key => key !== 'title')
              .reduce((obj: { [key: string]: any }, key) => {
                obj[key] = row[key];
                return obj;
              }, {}) : row,
            hiddenColsOnMobile,
            dataAutomation,
            noColsDivider,
            isFooterRow: footerRowIndexes?.includes(id),
            innerFooterRowIndexes: allInnerFooterRowIndexes ?
              allInnerFooterRowIndexes[id] : [],
            sxSmall
          };

          return (
            <CollapsibleTableContainer
              size={size}
              key={id}
              title={row.title}
              isThreeLevel={isThreeLevel}
              openByDefault={openByDefaultIndexes?.includes(id)}
              displayHeaderOnly={displayHeaderOnlyIndexes?.includes(id)}
            >
              <RowComp {...props} />
            </CollapsibleTableContainer>
          )
        })}
      </TableBody>
    </Table>
  );
};